import { cn } from "@/lib/utils"

interface ProgressCircleProps {
  value: number;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "secondary" | "accent";
  label?: string;
  sublabel?: string;
  className?: string;
}

const sizeConfig = {
  sm: { circle: 80, stroke: 6, fontSize: "text-lg", labelSize: "text-xs" },
  md: { circle: 120, stroke: 8, fontSize: "text-2xl", labelSize: "text-sm" },
  lg: { circle: 160, stroke: 10, fontSize: "text-4xl", labelSize: "text-base" },
};

const colorConfig = {
  primary: { 
    stroke: "stroke-primary", 
    from: "#5CE1E6",
    to: "#4FACFE",
    glowColor: "rgba(92, 225, 230, 0.5)"
  },
  secondary: { 
    stroke: "stroke-secondary", 
    from: "#FF6B7A",
    to: "#FF8E7B",
    glowColor: "rgba(255, 107, 122, 0.5)"
  },
  accent: { 
    stroke: "stroke-accent", 
    from: "#F4B942",
    to: "#F4D03F",
    glowColor: "rgba(244, 185, 66, 0.5)"
  },
};

export function ProgressCircle({ 
  value, 
  size = "md", 
  color = "primary",
  label,
  sublabel,
  className 
}: ProgressCircleProps) {
  const config = sizeConfig[size];
  const colors = colorConfig[color];
  const radius = (config.circle - config.stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  const gradientId = `gradient-${color}-${size}-${Math.random().toString(36).substr(2, 9)}`;
  const glowId = `glow-${color}-${size}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={cn("relative inline-flex flex-col items-center gap-3", className)}>
      <svg
        width={config.circle}
        height={config.circle}
        className="-rotate-90"
      >
        {/* Gradient definition */}
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.from} />
            <stop offset="100%" stopColor={colors.to} />
          </linearGradient>
          
          {/* Glow effect */}
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background circle */}
        <circle
          cx={config.circle / 2}
          cy={config.circle / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          className="text-muted-foreground/25"
        />
        
        {/* Progress circle with animation */}
        <circle
          cx={config.circle / 2}
          cy={config.circle / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={config.stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
          className="transition-all duration-1000 ease-out"
        />
        
        {/* Center value */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy=".3em"
          transform={`rotate(90 ${config.circle / 2} ${config.circle / 2})`}
          className={cn(config.fontSize, "font-bold fill-current")}
        >
          {value}%
        </text>
      </svg>
      
      {label && (
        <div className="max-w-[200px] text-center">
          <p className={cn(config.labelSize, "font-semibold text-foreground")}>{label}</p>
          {sublabel && (
            <p className="mt-1 text-xs text-muted-foreground">{sublabel}</p>
          )}
        </div>
      )}
    </div>
  );
}
